import React from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Plus } from 'lucide-react';
import { useEditMode } from '../context/EditModeContext';

/**
 * EditableList
 * --------------------------------------------------------------------
 * A generic sortable list. In view-mode it just maps items through
 * `renderItem`. In edit-mode it wraps each item in a sortable container
 * with a drag handle and delete button, and renders an optional "+ Add"
 * button below.
 *
 * Props:
 *   items       Array
 *   onChange    (newItems) => void
 *   renderItem  (item, idx, dragHandleProps) => JSX
 *   onAdd       ?() => void
 *   addLabel    ?string (default '+ Add')
 *   getKey      (item) => string  - REQUIRED, must be stable per item
 *   className   ?string
 */
function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  // We forward { attributes, listeners } as `dragHandleProps` so the consumer
  // can spread them onto whichever element they want to be the grab handle.
  return (
    <div ref={setNodeRef} style={style} className="relative group/row">
      {children({ attributes, listeners, isDragging })}
    </div>
  );
}

function EditableList({
  items = [],
  onChange,
  renderItem,
  onAdd,
  addLabel = '+ Add',
  getKey,
  className = '',
}) {
  const { editMode } = useEditMode();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!editMode) {
    return (
      <div className={className}>
        {items.map((item, idx) => (
          <React.Fragment key={getKey ? getKey(item) : idx}>
            {renderItem(item, idx, null)}
          </React.Fragment>
        ))}
      </div>
    );
  }

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => (getKey ? getKey(it) : '') === active.id);
    const newIndex = items.findIndex((it) => (getKey ? getKey(it) : '') === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange && onChange(arrayMove(items, oldIndex, newIndex));
  };

  const handleDelete = (idx) => {
    onChange && onChange(items.filter((_, i) => i !== idx));
  };

  const ids = items.map((it, idx) => (getKey ? getKey(it) : String(idx)));

  return (
    <div className={className}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {items.map((item, idx) => {
            const id = ids[idx];
            return (
              <SortableRow key={id} id={id}>
                {({ attributes, listeners }) => (
                  <div className="relative flex items-stretch gap-2 my-1 rounded hover:outline hover:outline-1 hover:outline-dashed hover:outline-indigo-400/70">
                    <button
                      type="button"
                      {...attributes}
                      {...listeners}
                      aria-label="Drag to reorder"
                      className="opacity-0 group-hover/row:opacity-100 transition-opacity inline-flex items-center justify-center w-6 h-6 self-center text-gray-400 hover:text-indigo-600 cursor-grab active:cursor-grabbing"
                    >
                      <GripVertical size={16} />
                    </button>
                    <div className="flex-1 min-w-0">
                      {renderItem(item, idx, { attributes, listeners })}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(idx)}
                      aria-label="Remove item"
                      className="opacity-0 group-hover/row:opacity-100 transition-opacity inline-flex items-center justify-center w-6 h-6 self-center text-gray-400 hover:text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </SortableRow>
            );
          })}
        </SortableContext>
      </DndContext>

      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-600 border border-dashed border-indigo-300 rounded hover:bg-indigo-50"
        >
          <Plus size={14} />
          {addLabel.replace(/^\+\s*/, '')}
        </button>
      )}
    </div>
  );
}

export default EditableList;
